import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/lib/supabase';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Calendar as CalendarIcon, 
  Dumbbell, 
  Clock,
  X,
  Save,
  CheckCircle,
  CalendarDays,
  Check
} from 'lucide-react';

interface User {
  id: string;
  full_name: string;
  email: string;
}

interface Exercise {
  id: string;
  name: string;
  description: string;
  created_by: string;
  created_at: string;
}

interface UserExercise {
  id: string;
  user_id: string;
  exercise_id: string;
  scheduled_date: string;
  sets: number;
  reps: number;
  weight?: string;
  exercises: Exercise;
}

interface DayExerciseManagerProps {
  selectedUser: string;
  users: User[];
  exercises: Exercise[];
  selectedDate: string;
  onClose: () => void;
  onExerciseUpdated: () => void;
}

const DayExerciseManager: React.FC<DayExerciseManagerProps> = ({
  selectedUser,
  users,
  exercises,
  selectedDate,
  onClose,
  onExerciseUpdated
}) => {
  const [dayExercises, setDayExercises] = useState<UserExercise[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingExercise, setEditingExercise] = useState<UserExercise | null>(null);

  const [formData, setFormData] = useState({
    exercise_id: '',
    sets: 3,
    reps: 10,
    weight: ''
  });

  const selectedUserData = users.find(u => u.id === selectedUser);

  // Load exercises for the selected date
  useEffect(() => {
    if (selectedUser && selectedDate) {
      loadDayExercises();
    }
  }, [selectedUser, selectedDate]);

  const loadDayExercises = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error } = await supabase
        .from('user_exercises')
        .select(`
          *,
          exercises (*)
        `)
        .eq('user_id', selectedUser)
        .eq('scheduled_date', selectedDate)
        .order('created_at', { ascending: true });

      if (error) {
        console.error('❌ Supabase error:', error);
        throw error;
      }

      setDayExercises(data || []);
    } catch (err) {
      console.error('❌ Error loading day exercises:', err);
      setError('Failed to load exercises for this day');
    } finally {
      setLoading(false);
    }
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    console.log('🚀 Form submitted with data:', formData);
    console.log('🚀 Selected user:', selectedUser);
    console.log('🚀 Selected date:', selectedDate);
    
    try {
      setError(null);
      setSuccess(null);

      // Validate required fields
      if (!formData.exercise_id) {
        console.log('❌ No exercise selected');
        setError('Please select an exercise type');
        return;
      }

      if (!formData.sets || formData.sets < 1) {
        console.log('❌ Invalid sets:', formData.sets);
        setError('Please enter a valid number of sets (minimum 1)');
        return;
      }

      if (!formData.reps || formData.reps < 1) {
        console.log('❌ Invalid reps:', formData.reps);
        setError('Please enter a valid number of reps (minimum 1)');
        return;
      }

      const exerciseData = {
        user_id: selectedUser,
        exercise_id: formData.exercise_id,
        scheduled_date: selectedDate,
        sets: formData.sets,
        reps: formData.reps,
        weight: formData.weight || null
      };

      console.log('🚀 Exercise data to save:', exerciseData);

      if (editingExercise) {
        // Update existing exercise
        console.log('🔄 Updating existing exercise:', editingExercise.id);
        const { error } = await supabase
          .from('user_exercises')
          .update(exerciseData)
          .eq('id', editingExercise.id);

        if (error) {
          console.error('❌ Update error:', error);
          throw error;
        }
        setSuccess('Exercise updated successfully!');
      } else {
        // Create new exercise
        console.log('➕ Creating new exercise');
        const { error } = await supabase
          .from('user_exercises')
          .insert([exerciseData]);

        if (error) {
          console.error('❌ Insert error:', error);
          throw error;
        }
        setSuccess('Exercise added successfully!');
      }

      console.log('✅ Exercise saved successfully');

      // Reset form and reload data
      setFormData({
        exercise_id: '',
        sets: 3,
        reps: 10,
        weight: ''
      });
      setShowAddForm(false);
      setEditingExercise(null);
      await loadDayExercises();
      onExerciseUpdated();
    } catch (err) {
      console.error('❌ Error saving exercise:', err);
      setError('Failed to save exercise');
    }
  };

  // Handle delete exercise
  const handleDeleteExercise = async (exerciseId: string) => {
    if (!confirm('Are you sure you want to delete this exercise?')) return;

    try {
      const { error } = await supabase
        .from('user_exercises')
        .delete()
        .eq('id', exerciseId);

      if (error) throw error;

      setSuccess('Exercise deleted successfully!');
      loadDayExercises();
      onExerciseUpdated();
    } catch (err) {
      console.error('Error deleting exercise:', err);
      setError('Failed to delete exercise');
    }
  };

  // Handle edit exercise
  const handleEditExercise = (exercise: UserExercise) => {
    setFormData({
      exercise_id: exercise.exercise_id,
      sets: exercise.sets,
      reps: exercise.reps,
      weight: exercise.weight || ''
    });
    setEditingExercise(exercise);
    setShowAddForm(true);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  if (loading) {
    return (
      <Dialog open={true} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            <span className="ml-2 text-muted-foreground">Loading exercises...</span>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="w-[95vw] sm:w-full max-w-2xl bg-background border-border">
        {/* Ultra Compact Header */}
        <DialogHeader className="pb-2 border-b border-border">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0">
            {/* Title Section */}
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                <CalendarIcon className="h-3 w-3 text-white" />
              </div>
              <div className="min-w-0 flex-1">
                <DialogTitle className="text-sm sm:text-base font-bold text-foreground truncate">
                  Exercise Manager
                </DialogTitle>
                <DialogDescription className="text-xs text-muted-foreground mt-0.5 truncate">
                  <span className="block sm:inline">{selectedUserData?.full_name || selectedUserData?.email}</span>
                  <span className="hidden sm:inline"> • </span>
                  <span className="block sm:inline">{formatDate(selectedDate)}</span>
                </DialogDescription>
              </div>
            </div>
            
            {/* Action Button - Always Visible */}
            <Button
              onClick={() => setShowAddForm(true)}
              className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white shadow-md hover:shadow-lg transition-all duration-200 px-2 py-1 text-xs font-medium w-full sm:w-auto mr-5"
            >
              <Plus className="h-3 w-3 mr-1" />
              <span className="sm:hidden">Add Exercise</span>
              <span className="hidden sm:inline">{dayExercises.length === 0 ? 'Add First Exercise' : 'Add Exercise'}</span>
            </Button>
          </div>
        </DialogHeader>

        <div className="overflow-y-auto">
          <div className="space-y-3 p-3 sm:p-4">
            {/* Error/Success Messages */}
            {error && (
              <Alert variant="destructive">
                <Clock className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {success && (
              <Alert className="border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950/20">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <AlertDescription className="text-green-800 dark:text-green-200">{success}</AlertDescription>
              </Alert>
            )}


            {/* Exercises List - Ultra Compact */}
            {dayExercises.length > 0 ? (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm sm:text-base font-semibold text-foreground">Scheduled Exercises</h4>
                  <Badge variant="outline" className="text-blue-600 text-xs">
                    {dayExercises.length} total
                  </Badge>
                </div>
                
                <div className="grid gap-2">
                  {dayExercises.map((exercise, index) => (
                    <Card key={exercise.id} className="group hover:shadow-lg transition-all duration-200 border-border bg-card">
                      <CardContent className="p-3 sm:p-4">
                        {/* Ultra Compact Layout */}
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0">
                          <div className="flex items-start gap-2">
                            <div className="w-6 h-6 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center shadow-md flex-shrink-0">
                              <Dumbbell className="h-3 w-3 text-white" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-0.5">
                                <h5 className="font-semibold text-foreground text-xs sm:text-sm truncate">{exercise.exercises.name}</h5>
                                <Badge variant="outline" className="text-xs flex-shrink-0">
                                  #{index + 1}
                                </Badge>
                              </div>
                              <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                                <span className="flex items-center gap-1">
                                  <div className="w-1 h-1 bg-blue-500 rounded-full"></div>
                                  {exercise.sets} sets
                                </span>
                                <span className="flex items-center gap-1">
                                  <div className="w-1 h-1 bg-green-500 rounded-full"></div>
                                  {exercise.reps} reps
                                </span>
                                {exercise.weight && (
                                  <span className="flex items-center gap-1">
                                    <div className="w-1 h-1 bg-orange-500 rounded-full"></div>
                                    {exercise.weight}kg
                                  </span>
                                )}
                              </div>
                              {exercise.exercises.description && (
                                <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                                  {exercise.exercises.description}
                                </p>
                              )}
                            </div>
                          </div>
                          
                          {/* Action Buttons - Always Visible on Mobile */}
                          <div className="flex items-center gap-2 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleEditExercise(exercise)}
                              className="h-7 sm:h-8 px-2 sm:px-3 text-blue-600 hover:text-blue-700 hover:bg-blue-50 border-blue-200 text-xs sm:text-sm flex-1 sm:flex-none"
                            >
                              <Edit className="h-3 w-3 mr-1" />
                              <span className="sm:inline">Edit</span>
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDeleteExercise(exercise.id)}
                              className="h-7 sm:h-8 px-2 sm:px-3 text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200 text-xs sm:text-sm flex-1 sm:flex-none"
                            >
                              <Trash2 className="h-3 w-3 mr-1" />
                              <span className="sm:inline">Delete</span>
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            ) : (
              <Card className="border-dashed border-2 border-border bg-card">
                <CardContent className="p-8 sm:p-12 text-center">
                  <div className="w-12 h-12 sm:w-16 sm:h-16 bg-muted rounded-xl sm:rounded-2xl flex items-center justify-center mx-auto mb-4 sm:mb-6">
                    <Dumbbell className="h-6 w-6 sm:h-8 sm:w-8 text-muted-foreground" />
                  </div>
                  <h3 className="text-lg sm:text-xl font-semibold text-foreground mb-2 sm:mb-3">No exercises scheduled</h3>
                  <p className="text-sm sm:text-base text-muted-foreground max-w-md mx-auto leading-relaxed">
                    Add exercises to this day to create a personalized workout plan and help {selectedUserData?.full_name || 'the user'} stay on track.
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Ultra Compact Professional Add Exercise Form */}
            {showAddForm && (
              <div className="fixed inset-0 bg-background z-20 flex flex-col max-h-screen overflow-hidden">
                {/* Debug Info */}
                {console.log('🚀 Form is open, showAddForm:', showAddForm, 'dayExercises.length:', dayExercises.length)}
                
                {/* Ultra Compact Header */}
                <div className="flex items-center justify-between p-2 border-b border-border bg-background flex-shrink-0">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
                      <Plus className="h-3 w-3 text-white" />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground">Add Exercise</h3>
                      <p className="text-xs text-muted-foreground">{formatDate(selectedDate)}</p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setShowAddForm(false);
                      setEditingExercise(null);
                      setFormData({ exercise_id: '', sets: 3, reps: 10, weight: '' });
                    }}
                    className="h-6 w-6 p-0"
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>

                {/* Ultra Compact Form Content */}
                <div className="flex-1 p-2 bg-background flex flex-col min-h-0">
                  <form onSubmit={handleSubmit} className="flex flex-col h-full">
                    {/* Form Content - Ultra Compact */}
                    <div className="flex-1 space-y-0">
                      {/* Exercise Selection - Ultra Compact */}
                      <div className="space-y-0">
                        <Label className="text-xs font-medium text-foreground">Exercise Type</Label>
                        <div className="relative">
                          <Input
                            type="text"
                            placeholder="Choose an exercise..."
                            value={formData.exercise_id ? exercises.find(e => e.id === formData.exercise_id)?.name || '' : ''}
                            onChange={(e) => {
                              const searchTerm = e.target.value.toLowerCase();
                              const foundExercise = exercises.find(ex => 
                                ex.name.toLowerCase().includes(searchTerm)
                              );
                              if (foundExercise) {
                                setFormData(prev => ({ ...prev, exercise_id: foundExercise.id }));
                              } else {
                                setFormData(prev => ({ ...prev, exercise_id: '' }));
                              }
                            }}
                            className="h-7 text-xs"
                          />
                          {formData.exercise_id && (
                            <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                              <div className="w-2 h-2 bg-green-500 rounded-full flex items-center justify-center">
                                <Check className="h-1 w-1 text-white" />
                              </div>
                            </div>
                          )}
                        </div>
                        
                        {/* Ultra Compact Exercise Cards */}
                        <div className="grid grid-cols-4 gap-0.5 max-h-20 overflow-y-auto">
                          {exercises.slice(0, 8).map((exercise) => (
                            <button
                              key={exercise.id}
                              type="button"
                              onClick={() => setFormData(prev => ({ ...prev, exercise_id: exercise.id }))}
                            className={`p-0.5 rounded border text-center transition-colors duration-150 focus:outline-none focus:ring-1 focus:ring-blue-500/50 ${
                              formData.exercise_id === exercise.id
                                ? 'border-blue-500 bg-blue-500/20 shadow-sm ring-1 ring-blue-500/20'
                                : 'border-gray-200 dark:border-gray-700 hover:border-blue-300 hover:bg-blue-500/10 dark:hover:bg-blue-500/10'
                            }`}
                            >
                              <div className="flex flex-col items-center gap-0.5">
                                <div className={`w-2 h-2 rounded flex items-center justify-center ${
                                  formData.exercise_id === exercise.id
                                    ? 'bg-blue-500'
                                    : 'bg-gray-200 dark:bg-gray-700'
                                }`}>
                                  <Dumbbell className={`h-1 w-1 ${
                                    formData.exercise_id === exercise.id ? 'text-white' : 'text-gray-600 dark:text-gray-300'
                                  }`} />
                                </div>
                                <span className={`text-xs font-bold truncate w-full leading-tight ${
                                  formData.exercise_id === exercise.id 
                                    ? 'text-blue-600 dark:text-blue-400' 
                                    : 'text-foreground'
                                }`}>{exercise.name}</span>
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Sets and Reps Row - Ultra Compact */}
                      <div className="flex gap-2">
                        {/* Sets */}
                        <div className="flex-1">
                          <Label className="text-xs font-medium text-foreground mb-0.5 block">Sets</Label>
                          <div className="flex items-center gap-1">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => setFormData(prev => ({ ...prev, sets: Math.max(1, prev.sets - 1) }))}
                              className="h-7 w-7 text-xs font-bold focus:outline-none focus:ring-1 focus:ring-blue-500/50"
                            >
                              -
                            </Button>
                            <div className="flex-1 text-center">
                              <div className="text-base font-bold text-foreground">{formData.sets}</div>
                            </div>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => setFormData(prev => ({ ...prev, sets: Math.min(50, prev.sets + 1) }))}
                              className="h-7 w-7 text-xs font-bold focus:outline-none focus:ring-1 focus:ring-blue-500/50"
                            >
                              +
                            </Button>
                          </div>
                        </div>

                        {/* Reps */}
                        <div className="flex-1">
                          <Label className="text-xs font-medium text-foreground mb-0.5 block">Reps</Label>
                          <div className="flex items-center gap-1">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => setFormData(prev => ({ ...prev, reps: Math.max(1, prev.reps - 1) }))}
                              className="h-7 w-7 text-xs font-bold focus:outline-none focus:ring-1 focus:ring-blue-500/50"
                            >
                              -
                            </Button>
                            <div className="flex-1 text-center">
                              <div className="text-base font-bold text-foreground">{formData.reps}</div>
                            </div>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => setFormData(prev => ({ ...prev, reps: Math.min(100, prev.reps + 1) }))}
                              className="h-7 w-7 text-xs font-bold focus:outline-none focus:ring-1 focus:ring-blue-500/50"
                            >
                              +
                            </Button>
                          </div>
                        </div>
                      </div>

                      {/* Weight - Ultra Compact */}
                      <div>
                        <Label className="text-xs font-medium text-foreground mb-0 block">Weight (kg)</Label>
                        <Input
                          type="number"
                          step="0.5"
                          min={0}
                          max={500}
                          placeholder="Enter weight (optional)"
                          value={formData.weight}
                          onChange={(e) => setFormData(prev => ({ ...prev, weight: e.target.value }))}
                          className="h-7 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500/50"
                        />
                      </div>

                      {/* Action Buttons - Ultra Compact */}
                      <div className="flex gap-2 pb-1 border-t border-border bg-background flex-shrink-0">
                      {console.log('🚀 Action buttons section rendered')}
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          setShowAddForm(false);
                          setEditingExercise(null);
                          setFormData({ exercise_id: '', sets: 3, reps: 10, weight: '' });
                        }}
                        className="flex-1 h-8 text-xs"
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        onClick={(e) => {
                          console.log('🚀 Add Exercise button clicked');
                          if (!formData.exercise_id) {
                            e.preventDefault();
                            setError('Please select an exercise type');
                            return;
                          }
                        }}
                        disabled={!formData.exercise_id}
                        className="flex-1 h-8 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white text-xs font-medium disabled:opacity-50"
                      >
                        <Save className="h-3 w-3 mr-1" />
                        Add Exercise
                      </Button>
                    </div>
                    </div>
                  </form>
                </div>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DayExerciseManager;
